/**
 * Collection Filters Drawer, Badges & Sort
 * Gestion de toutes les interactions de filtrage pour les pages de collection.
 */

class CollectionFilters {
  constructor() {
    this.drawer = document.querySelector('[data-filters-drawer]');
    if (!this.drawer) return;

    // Éléments du Drawer
    this.overlay = document.querySelector('[data-filters-overlay]');
    this.toggleButton = document.querySelector('[data-filters-toggle]');
    this.closeButton = document.querySelector('[data-filters-close]');
    this.applyButton = document.querySelector('[data-filters-apply]');
    this.clearButton = document.querySelector('[data-filters-clear]');
    this.filterCountElement = document.querySelector('[data-filter-count]');

    // Éléments des Badges
    this.badgesContainer = document.querySelector('[data-active-filters-container]');
    this.badgesList = this.badgesContainer?.querySelector('[data-active-filters-list]');
    this.clearAllBadgesButton = this.badgesContainer?.querySelector('[data-clear-all-filters]');

    this.isOpen = false;
    this.activeUrlFilters = new Map();
    
    this.init();
  }

  init() {
    this.loadActiveFiltersFromURL();
    this.bindGlobalEvents();
    this.bindDrawerEvents();
    this.renderBadges();
    this.updateFilterCounts();
  }

  // --- GESTION DES ÉVÉNEMENTS ---

  bindGlobalEvents() {
    this.toggleButton?.addEventListener('click', () => this.openDrawer());
    this.clearAllBadgesButton?.addEventListener('click', () => this.clearAllFilters());
    document.querySelector('.collection-empty__button')?.addEventListener('click', () => this.clearAllFilters());
    this.badgesList?.addEventListener('click', (event) => {
      const removeButton = event.target.closest('.active-filter-badge__remove');
      if (removeButton) {
        this.removeFilter(removeButton.dataset.key, removeButton.dataset.value);
      }
    });
  }

  bindDrawerEvents() {
    this.closeButton?.addEventListener('click', () => this.closeDrawer());
    this.overlay?.addEventListener('click', () => this.closeDrawer());
    this.applyButton?.addEventListener('click', () => this.applyFilters());
    this.clearButton?.addEventListener('click', () => this.clearDrawerForm());
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) this.closeDrawer();
    });
  }

  // --- GESTION DU DRAWER ---

  openDrawer() {
    this.drawer.style.display = 'flex';
    this.overlay.style.display = 'block';
    this.drawer.offsetHeight; // Force reflow
    this.drawer.classList.add('is-open');
    this.overlay.classList.add('is-visible');
    this.isOpen = true;
    document.body.style.overflow = 'hidden';
    this.drawer.querySelector('button, input, [tabindex="0"]')?.focus();
  }

  closeDrawer() {
    this.drawer.classList.remove('is-open');
    this.overlay.classList.remove('is-visible');
    this.isOpen = false;
    document.body.style.overflow = '';
    setTimeout(() => {
      if (!this.isOpen) {
        this.drawer.style.display = 'none';
        this.overlay.style.display = 'none';
      }
    }, 300);
  }

  clearDrawerForm() {
    this.drawer.querySelectorAll('input').forEach(input => {
      if (input.type === 'checkbox' || input.type === 'radio') {
        input.checked = false;
      } else {
        input.value = '';
      }
    });
  }

  // --- GESTION DES FILTRES & URL ---

  applyFilters() {
    this.applyButton?.classList.add('filters-drawer--loading');
    const formData = new FormData(this.drawer.querySelector('form'));
    const currentParams = new URLSearchParams(window.location.search);
    
    // On garde le tri actuel
    const newParams = new URLSearchParams();
    if (currentParams.has('sort_by')) {
      newParams.set('sort_by', currentParams.get('sort_by'));
    }

    // On ajoute tous les filtres du formulaire
    formData.forEach((value, key) => {
      if (value) {
        newParams.append(key, value);
      }
    });

    window.location.search = newParams.toString();
  }

  removeFilter(keyToRemove, valueToRemove) {
    const params = new URLSearchParams(window.location.search);
    const allValues = params.getAll(keyToRemove);
    
    if (allValues.length === 1) {
      params.delete(keyToRemove);
    } else {
      params.delete(keyToRemove);
      allValues.forEach(val => {
        if (val !== valueToRemove) params.append(keyToRemove, val);
      });
    }
    window.location.search = params.toString();
  }
  
  clearAllFilters() {
    const params = new URLSearchParams(window.location.search);
    const sortBy = params.get('sort_by'); // Conserver le tri
    const newParams = new URLSearchParams();
    if (sortBy) newParams.set('sort_by', sortBy);
    window.location.search = newParams.toString();
  }
  
  // --- MISE À JOUR DE L'UI ---

  loadActiveFiltersFromURL() {
    this.activeUrlFilters.clear();
    const params = new URLSearchParams(window.location.search);
    params.forEach((value, key) => {
      if (key.startsWith('filter.')) {
        if (!this.activeUrlFilters.has(key)) {
          this.activeUrlFilters.set(key, new Set());
        }
        this.activeUrlFilters.get(key).add(value);
      }
    });
    this.syncDrawerFormWithURL();
  }

  syncDrawerFormWithURL() {
    this.activeUrlFilters.forEach((values, key) => {
      values.forEach(value => {
        const input = this.drawer.querySelector(`[name="${key}"][value="${value}"]`);
        if (input) input.checked = true;

        if (key.includes('price.gte')) this.drawer.querySelector('[data-price-min]').value = value;
        if (key.includes('price.lte')) this.drawer.querySelector('[data-price-max]').value = value;
      });
    });
  }
  
  updateFilterCounts() {
    const count = this.activeUrlFilters.size > 0 ? Array.from(this.activeUrlFilters.values()).reduce((acc, set) => acc + set.size, 0) : 0;
    
    if (this.filterCountElement) {
      this.filterCountElement.textContent = `(${count})`;
      this.filterCountElement.style.display = count > 0 ? 'inline-block' : 'none';
    }
    const mainToggleCount = document.querySelector('[data-active-filters-count]');
    if (mainToggleCount) {
        mainToggleCount.textContent = `(${count})`;
        mainToggleCount.style.display = count > 0 ? 'inline-block' : 'none';
    }
  }

  renderBadges() {
    if (!this.badgesList) return;
    this.badgesList.innerHTML = '';
    let filterCount = 0;

    this.activeUrlFilters.forEach((values, key) => {
      values.forEach(value => {
        filterCount++;
        const label = this.getBadgeLabel(key, value);
        const badge = this.createBadge(label, key, value);
        this.badgesList.appendChild(badge);
      });
    });
    this.badgesContainer.style.display = filterCount > 0 ? 'flex' : 'none';
  }

  getBadgeLabel(key, value) {
    // Essayer de trouver le label depuis le formulaire
    const input = this.drawer.querySelector(`input[name="${key}"][value="${value}"]`);
    if (input) {
      const labelElement = input.closest('.filter-option')?.querySelector('.filter-option__label');
      if (labelElement) return labelElement.textContent.trim();
    }
    
    // Fallback pour les prix
    if (key.endsWith('.gte')) return `Min: ${value} ${window.Shopify?.currency?.active || ''}`;
    if (key.endsWith('.lte')) return `Max: ${value} ${window.Shopify?.currency?.active || ''}`;
    
    // Fallback pour les autres filtres (ex: tags)
    return value.replace(/[-_]/g, ' ');
  }

  createBadge(label, key, value) {
    const badgeElement = document.createElement('div');
    badgeElement.className = 'active-filter-badge';
    badgeElement.innerHTML = `
      <span class="active-filter-badge__text">${label}</span>
      <button type="button" class="active-filter-badge__remove" data-key="${key}" data-value="${value}" aria-label="Supprimer le filtre ${label}">
        <svg width="8" height="8" viewBox="0 0 8 8" fill="none"><path d="M1 1L7 7M7 1L1 7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"></svg>
      </button>`;
    return badgeElement;
  }
}

class CollectionSort {
  constructor() {
    this.sortSelect = document.querySelector('[data-collection-sort]');
    this.init();
  }

  init() {
    this.sortSelect?.addEventListener('change', (e) => this.applySorting(e.target.value));
  }

  applySorting(sortValue) {
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', sortValue);
    window.location = url.href;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new CollectionFilters();
  new CollectionSort();
});

// Optimistic UI pour les actions rapides
document.addEventListener('click', (e) => {
  // Gestion des favoris
  if (e.target.closest('.product-action--wishlist')) {
    const button = e.target.closest('.product-action--wishlist');
    button.classList.toggle('is-active');
    
    // Animation de feedback
    button.style.transform = 'scale(1.2)';
    setTimeout(() => {
      button.style.transform = '';
    }, 150);
  }
  
  // Gestion de l'ajout rapide au panier
  if (e.target.closest('.product-action--quick-add')) {
    const button = e.target.closest('.product-action--quick-add');
    const productId = button.dataset.productId;
    const variantId = button.dataset.variantId;
    
    if (variantId) {
      button.classList.add('is-loading');
      button.innerHTML = '<div class="spinner"></div>';
      
      // Simulation d'ajout au panier (à remplacer par l'API réelle)
      setTimeout(() => {
        button.classList.remove('is-loading');
        button.classList.add('is-added');
        button.innerHTML = '✓';
        
        setTimeout(() => {
          button.classList.remove('is-added');
          button.innerHTML = `<svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M10 4V16M4 10H16" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>`;
        }, 2000);
      }, 500);
    }
  }
});

// CSS pour les animations ajoutées dynamiquement
const style = document.createElement('style');
style.textContent = `
  .product-action--wishlist.is-active {
    color: #EA4335 !important;
  }
  
  .product-action--quick-add.is-loading {
    pointer-events: none;
  }
  
  .product-action--quick-add.is-added {
    background: #34A853 !important;
    color: white !important;
  }
  
  .spinner {
    width: 16px;
    height: 16px;
    border: 2px solid transparent;
    border-top: 2px solid currentColor;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;
document.head.appendChild(style); 